import { BasicFormProps } from './BasicFormProps';
import { AbstractFormContentModel, CreativeFormData } from './FormContentModel';
import _ from 'lodash';
import i18n from 'i18next';
import { Creative, CreativeStatus, RichMediaTemplateId } from 'core/creative/Creative';
import { LocaleMetaStore } from 'core';
import { Product } from 'core/product/Product';
import { CreativeSetupFlowPageModel } from 'containers/Creatives/CreativeSetupFlow/CreativeSetupFlowPageModelInterface';
import { formatBytes } from 'utils/StringUtil';
import { validateUrl } from 'utils/ValidateUtils';
import { ImageFormModel } from './ImageFormModel';
import { ProductNativeFormModel } from './ProductNativeFormModel';
import { renderErrors } from './CreativeFormHintRenderFunction';
import RichMediaForm from './RichMediaForm';

export enum RichMediaType {
  IMAGE_IMAGE = 'IMAGE_IMAGE',
  IMAGE_VIDEO = 'IMAGE_VIDEO',
  MARQUEE_PC = 'MARQUEE_PC',
  MARQUEE_MOBILE = 'MARQUEE_MOBILE'
}

export enum RichMediaDeliveryPosition {
  IN_SITE = 'IN_SITE',
  OFF_SITE = 'OFF_SITE'
}

// 版型 ↔ template ID 對照；id 本身定義在 core/creative/Creative 的 RichMediaTemplateId
export const RICH_MEDIA_TEMPLATE_ID: { [key in RichMediaType]: string } = {
  [RichMediaType.IMAGE_IMAGE]: RichMediaTemplateId.EXPAND_IMAGE,
  [RichMediaType.IMAGE_VIDEO]: RichMediaTemplateId.EXPAND_VIDEO,
  [RichMediaType.MARQUEE_PC]: RichMediaTemplateId.MARQUEE_PC,
  [RichMediaType.MARQUEE_MOBILE]: RichMediaTemplateId.MARQUEE_MOBILE
};

// 編輯回填時由 creativeValues.templateId 反推版型（四個 template id 明確 4 選 1）
export const RICH_MEDIA_TYPE_BY_TEMPLATE_ID: { [templateId: string]: RichMediaType } = {
  [RICH_MEDIA_TEMPLATE_ID[RichMediaType.IMAGE_IMAGE]]: RichMediaType.IMAGE_IMAGE,
  [RICH_MEDIA_TEMPLATE_ID[RichMediaType.IMAGE_VIDEO]]: RichMediaType.IMAGE_VIDEO,
  [RICH_MEDIA_TEMPLATE_ID[RichMediaType.MARQUEE_PC]]: RichMediaType.MARQUEE_PC,
  [RICH_MEDIA_TEMPLATE_ID[RichMediaType.MARQUEE_MOBILE]]: RichMediaType.MARQUEE_MOBILE
};

export const RICH_MEDIA_TYPE_I18N_KEY: { [key in RichMediaType]: string } = {
  [RichMediaType.IMAGE_IMAGE]: 'creativeSetupFlow.labels.richMediaTypeImageImage',
  [RichMediaType.IMAGE_VIDEO]: 'creativeSetupFlow.labels.richMediaTypeImageVideo',
  [RichMediaType.MARQUEE_PC]: 'creativeSetupFlow.labels.richMediaTypeMarqueePc',
  [RichMediaType.MARQUEE_MOBILE]: 'creativeSetupFlow.labels.richMediaTypeMarqueeMobile'
};

// 置底圖 600 x 100、主要圖 600 x 300
export const RICH_MEDIA_BAR_IMAGE_SIZE = { width: 600, height: 100 };
export const RICH_MEDIA_EXPAND_IMAGE_SIZE = { width: 600, height: 300 };
// 跑馬燈：桌機 728 x 90、行動裝置 320 x 50，三張固定順序的輪播圖
export const RICH_MEDIA_MARQUEE_PC_IMAGE_SIZE = { width: 728, height: 90 };
export const RICH_MEDIA_MARQUEE_MOBILE_IMAGE_SIZE = { width: 320, height: 50 };
export const MARQUEE_IMAGE_COUNT = 3;
// 跑馬燈限 PNG / JPG；image/jpg 是使用者明確要求要接受的（部分瀏覽器會給這個 MIME）
export const RICH_MEDIA_MARQUEE_VALID_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];
// 影片：16 : 9、mp4、<= 5MB
export const RICH_MEDIA_VIDEO_RATIO = 16 / 9;
export const RICH_MEDIA_VIDEO_MAX_SIZE = 5 * 1024 * 1024;
const VIDEO_RATIO_TOLERANCE = 0.02;

export const isMarqueeRichMediaType = (type: RichMediaType): boolean =>
  type === RichMediaType.MARQUEE_PC || type === RichMediaType.MARQUEE_MOBILE;

// 只對跑馬燈版型有意義；非跑馬燈版型會拿到桌機尺寸，呼叫端須自行確認 isMarqueeRichMediaType。
export const getMarqueeImageSize = (type: RichMediaType) =>
  type === RichMediaType.MARQUEE_MOBILE
    ? RICH_MEDIA_MARQUEE_MOBILE_IMAGE_SIZE
    : RICH_MEDIA_MARQUEE_PC_IMAGE_SIZE;

export const getMarqueeImageMediaKey = (index: number): string => `marqueeImage${index + 1}`;

// 站內導外連結限定 https://xxx.pchome.com.tw
const IN_SITE_URL_REGEX = /^https:\/\/(?:[a-zA-Z0-9-]+\.)*pchome\.com\.tw(?:\/.*)?$/;

export type RichMediaFormProps = {
  readonly model: RichMediaFormModel;
  readonly hintModalData?: any;
  readonly setHintModalData: (modalData: any) => void;
} & BasicFormProps;

export class RichMediaFormModel extends AbstractFormContentModel {

  public needBannerUrl: boolean = false;
  public barImageFormModel: ImageFormModel;
  public expandImageFormModel: ImageFormModel;
  // 跑馬燈三張圖各自一個 ImageFormModel（沿用同一套 1MB / exact size 驗證機制），
  // 尺寸限制與 validTypes 依當前版型套用，見 applyMarqueeImageRestriction。
  public marqueeImageFormModels: ImageFormModel[];
  public validateMarqueeImages: ((fileData: any) => any)[];
  public productNativeFormModel: ProductNativeFormModel;
  public videoValidTypes: string[] = ['video/mp4'];
  richMediaType: RichMediaType = RichMediaType.IMAGE_IMAGE;
  deliveryPosition: RichMediaDeliveryPosition = RichMediaDeliveryPosition.IN_SITE;

  constructor (
    public flowPageModel: CreativeSetupFlowPageModel,
    products: Product[] = []
  ) {
    super(flowPageModel);
    this.barImageFormModel = new ImageFormModel(flowPageModel, {
      [RICH_MEDIA_BAR_IMAGE_SIZE.width]: [RICH_MEDIA_BAR_IMAGE_SIZE.height]
    });
    this.expandImageFormModel = new ImageFormModel(flowPageModel, {
      [RICH_MEDIA_EXPAND_IMAGE_SIZE.width]: [RICH_MEDIA_EXPAND_IMAGE_SIZE.height]
    });
    this.marqueeImageFormModels = _.times(MARQUEE_IMAGE_COUNT, () => {
      const imageFormModel = new ImageFormModel(flowPageModel, {});
      imageFormModel.validTypes = RICH_MEDIA_MARQUEE_VALID_TYPES;
      imageFormModel.typeErrorHintKey = 'creativeSetupFlow.labels.richMediaMarqueeImageTypeErrorHint';
      return imageFormModel;
    });
    // 表單欄位的 validate 需要穩定的 function identity（會進 useEffect 依賴），故只建一次
    this.validateMarqueeImages = this.marqueeImageFormModels.map(
      imageFormModel => (fileData) => imageFormModel.validateImage(fileData)
    );
    const creativeTypeProperties = _.get(flowPageModel.state.creative, 'basic.typeProperties');
    const initProducts = this.flowPageModel.type === 'edit'
      ? _.defaultTo(_.get(creativeTypeProperties, 'products'), [])
      : products;
    this.richMediaType = _.get(creativeTypeProperties, 'richMediaType', RichMediaType.IMAGE_IMAGE);
    this.deliveryPosition = _.get(creativeTypeProperties, 'deliveryPosition', RichMediaDeliveryPosition.IN_SITE);
    this.productNativeFormModel = new ProductNativeFormModel(flowPageModel, initProducts);
    this.applyMarqueeImageRestriction();
  }

  // 三張圖的尺寸限制隨版型切換（桌機 728 x 90 / 行動裝置 320 x 50）；
  // validTypes 固定為 PNG / JPG，在 constructor 設一次即可。
  private applyMarqueeImageRestriction () {
    const size = getMarqueeImageSize(this.richMediaType);
    this.marqueeImageFormModels.forEach(imageFormModel => {
      imageFormModel.validWidthHeightMap = { [size.width]: [size.height] };
    });
  }

  // Rich Media 站外投遞是否可用，由 agency addonProps.richMediaOffSite 控管
  get allowOffSite (): boolean {
    return LocaleMetaStore.getInstance().get()?.property?.addonProps?.richMediaOffSite ?? false;
  }

  get templateId (): string {
    return RICH_MEDIA_TEMPLATE_ID[this.richMediaType];
  }

  get isVideoType (): boolean {
    return this.richMediaType === RichMediaType.IMAGE_VIDEO;
  }

  get isMarqueeType (): boolean {
    return isMarqueeRichMediaType(this.richMediaType);
  }

  get isInSite (): boolean {
    return this.deliveryPosition === RichMediaDeliveryPosition.IN_SITE;
  }

  setRichMediaType = (type: RichMediaType) => {
    this.richMediaType = type;
    this.applyMarqueeImageRestriction();
  }

  setDeliveryPosition = (position: RichMediaDeliveryPosition) => {
    this.deliveryPosition = position;
  }

  getInitTypeProperties (): any {
    return {
      richMediaType: this.richMediaType,
      deliveryPosition: this.deliveryPosition,
      products: this.productNativeFormModel.products
    };
  }

  getFormContent = (): any => {
    return RichMediaForm;
  }

  validateBarImage = (fileData) => {
    return this.barImageFormModel.validateImage(fileData);
  }

  validateExpandImage = (fileData) => {
    return this.expandImageFormModel.validateImage(fileData);
  }

  validateVideo = (fileData) => {
    if (!fileData || (!fileData.file && !fileData.url)) {
      return i18n.t<string>('formValidate.labels.emptyError');
    }
    if (!fileData.file) {
      return;
    }

    const file = fileData.file;
    if (this.videoValidTypes.indexOf(file.type) === -1) {
      return i18n.t<string>('creativeSetupFlow.labels.videoTypeHint');
    }

    const errors: string[] = [];
    const width = fileData.width;
    const height = fileData.height;
    if (width && height) {
      const ratio = width / height;
      if (Math.abs(ratio - RICH_MEDIA_VIDEO_RATIO) > VIDEO_RATIO_TOLERANCE) {
        errors.push(i18n.t<string>('creativeSetupFlow.labels.videoRatioHint', { ratio: '16:9' }));
      }
    }
    if (file.size > RICH_MEDIA_VIDEO_MAX_SIZE) {
      errors.push(i18n.t<string>('creativeSetupFlow.labels.storageErrorHint', { storage1: formatBytes(file.size), storage2: '5MB' }));
    }

    return errors.length > 0 ? renderErrors(errors) : undefined;
  }

  validateLandingPageUrl = (url) => {
    const error = validateUrl(url);
    if (error) {
      return error;
    }
    if (!this.isInSite) {
      return;
    }
    if (!IN_SITE_URL_REGEX.test(url)) {
      return i18n.t<string>('creativeSetupFlow.labels.richMediaInSiteUrlErrorHint');
    }
  }

  validateProducts = () => {
    const products = this.productNativeFormModel.products;
    if (this.isInSite && products.length === 0) {
      return i18n.t<string>('productNativeCreativeForm.errors.cannotBeEmpty');
    }
    return undefined;
  }

  getFormModelData (creative: Creative): CreativeFormData {
    const creativeBasic: any = _.omit(creative.basic, ['id', 'srcType', 'creativeValues']);
    const creativeValue = JSON.parse(_.get(creative, 'basic.creativeValues', '{}'));
    const templateId: string = _.get(creativeValue, 'templateId');
    const richMediaType = RICH_MEDIA_TYPE_BY_TEMPLATE_ID[templateId] || RichMediaType.IMAGE_IMAGE;
    const deliveryPosition = _.get(creativeValue, 'deliveryPosition', RichMediaDeliveryPosition.IN_SITE);
    this.richMediaType = richMediaType;
    this.deliveryPosition = deliveryPosition;
    this.applyMarqueeImageRestriction();

    const isVideo = richMediaType === RichMediaType.IMAGE_VIDEO;
    const medias: any = {};
    if (isMarqueeRichMediaType(richMediaType)) {
      // 持久化用短名（imageUrl / w / h），順序即輪播順序
      const marqueeImages = _.get(creativeValue, 'marqueeImages', []);
      _.times(MARQUEE_IMAGE_COUNT, (index) => {
        const marqueeImageData = _.get(marqueeImages, index);
        if (!marqueeImageData) {
          return;
        }
        medias[getMarqueeImageMediaKey(index)] = _.omitBy({
          url: _.get(marqueeImageData, 'imageUrl'),
          width: _.get(marqueeImageData, 'w'),
          height: _.get(marqueeImageData, 'h')
        }, _.isUndefined);
      });
    } else {
      const barImageData = _.get(creativeValue, 'barImage', {});
      medias.barImage = _.omitBy({
        url: _.get(barImageData, 'imageUrl'),
        width: _.get(barImageData, 'w'),
        height: _.get(barImageData, 'h')
      }, _.isUndefined);
      if (isVideo) {
        const expandVideoData = _.get(creativeValue, 'expandVideo', {});
        medias.expandVideo = _.omitBy({
          url: _.get(expandVideoData, 'videoUrl'),
          videoAssetId: _.get(expandVideoData, 'videoAssetId'),
          width: _.get(expandVideoData, 'w'),
          height: _.get(expandVideoData, 'h'),
          duration: _.get(expandVideoData, 'duration')
        }, _.isUndefined);
      } else {
        const expandImageData = _.get(creativeValue, 'expandImage', {});
        medias.expandImage = _.omitBy({
          url: _.get(expandImageData, 'imageUrl'),
          width: _.get(expandImageData, 'w'),
          height: _.get(expandImageData, 'h')
        }, _.isUndefined);
      }
    }

    const products =
      creative.basic.relatedProductInfo
        ?.filter((product) => product.state !== CreativeStatus.DEACTIVE)
        .map((product) => ({
          productId: product.id,
          ...product
        })) || [];

    return {
      basic: {
        ...creativeBasic,
        typeProperties: {
          richMediaType,
          deliveryPosition,
          products
        },
        medias
      }
    };
  }
}
